import React from "react";

import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import FormattedDate from "@cayuse/shared-react.components.formatted-date";

import {
    ERROR_RESPONSE_SERVICE_UNAVAILABLE,
    ERROR_STATUS_CODE_406,
    ERROR_STATUS_CODE_200,
    FIELD_TYPES,
    PROPOSALS_PRODUCT_KEY,
    ERROR_STATUS_CODE_401,
    ERROR_STATUS_CODE_404,
} from "globals/constants";
import { api } from "services";
import { findUsers } from "./users";
import { notifyError } from "./errorNotification";
import store from "store/store";

import styles from "components/flowable/ProposalsS2SRecord/styles.css";
import { findUnitById } from "./picklists";

export const S2S_RECORD_KEY = "Proposals (S2S) Record";

export const s2sErrorHandler = (error, dictionary, DICTIONARY) => {
    const hasServiceError =
        error && isNonEmptyString(error.message)
            ? error.message.includes(ERROR_RESPONSE_SERVICE_UNAVAILABLE)
            : false;

    let serviceError = "";
    if (error.status === ERROR_STATUS_CODE_401) {
        notifyError(
            "You are not authorized to make this action.  Contact a Proposals (S2S) Administrator for assistance.",
            error
        );
        return;
    }

    if (error.status === ERROR_STATUS_CODE_404) {
        notifyError(
            "This opportunity no longer exists in Proposals S2S.",
            error
        );
        return;
    }

    if (error.response.status === ERROR_STATUS_CODE_406) {
        serviceError = dictionary.noAccess ?? DICTIONARY.noAccess;
    } else if (hasServiceError) {
        serviceError =
            dictionary.serviceUnavailable ?? DICTIONARY.serviceUnavailable;
        serviceError = `${serviceError} ${
            dictionary.contactAdminstrator ?? DICTIONARY.contactAdminstrator
        }`;
    } else {
        serviceError = dictionary.errorGeneric ?? DICTIONARY.errorGeneric;
        serviceError = `${serviceError} ${
            dictionary.contactAdminstrator ?? DICTIONARY.contactAdminstrator
        }`;
    }

    return serviceError;
};

export const queryOpportunities = (search, url) => {
    return api.s2s
        .findAll({ search, page: 0, size: 20 }, url, {})
        .then((response) => {
            if (response.status && response.status !== ERROR_STATUS_CODE_200) {
                s2sErrorHandler(response);
                return;
            }
            const options = (response.items ?? []).map((item) => {
                return {
                    key: item.opportunityNumber,
                    value: item.opportunityNumber,
                    text: item.opportunityTitle,
                    opportunity: item,
                    content: (
                        <>
                            <div>{item.opportunityNumber}</div>
                            <div className={styles.opportunityTitle}>
                                {item.opportunityTitle}
                            </div>
                            <div>Package ID: {item.packageId}</div>
                            <div>
                                Close Date:{" "}
                                <FormattedDate date={item.closeDate} />
                            </div>
                        </>
                    ),
                };
            });

            return {
                options,
                hiddenOptionsCount:
                    response.totalItems > response.size &&
                    response.totalItems - response.size,
            };
        })
        .catch((error) => {
            s2sErrorHandler(error);
            return {
                options: [],
            };
        });
};

const getResearchTeamData = async (rtoString) => {
    if (!rtoString) {
        return {};
    }

    try {
        const rtoList = JSON.parse(rtoString);
        const rto = rtoList.find((member) => member.role === "principal-investigator");

        if (!rto || !rto.member || !rto.associations) {
            return {};
        }

        let piAffiliationCode = null;
        if (rto.associations.length > 1) {
            const affiliations = await api.unit.getAdminPersonAffiliations(rto.member);
            const unitId = affiliations.find(affiliation => !!affiliation.primaryAppointment)?.unitId;

            const hasPrimaryAppointment = !!rto.associations.find(
                association => association.association === unitId
            );
            
            if (hasPrimaryAppointment) {
                const unit = await findUnitById(unitId);
                piAffiliationCode = unit.primaryCode;
            }
        } else {
            const associationId = rto.associations[0].association;
            const unit = await findUnitById(associationId);
            piAffiliationCode = unit.primaryCode;
        }
        
        const person = await api.person.get({ id: rto.member });

        return {
            piUserId: piAffiliationCode ? person.username : null,
            piAffiliationCode,
        };
    } catch {
        return {};
    }
};

export const getProposalData = async (
    proposal,
    opportunity,
    referenceIdUrl
) => {
    const refURL = new URL(referenceIdUrl);
    const refId = refURL.searchParams.get("refId");
    const proposalActualValues = store.getState().proposal.content.values.data;

    const proposalTitleId = proposal.formElements.find(
            (element) => element.id.toLowerCase() === "proposal-title"
        )?.id,
        proposalTitle = proposalActualValues[proposalTitleId];

    const projectStartDateId = proposal.formElements.find(
            (element) => element.id.toLowerCase() === "project-start-date"
        )?.id,
        projectStartDate = proposalActualValues[projectStartDateId];
    const projectEndDateId = proposal.formElements.find(
            (element) => element.id.toLowerCase() === "project-end-date"
        )?.id,
        projectEndDate = proposalActualValues[projectEndDateId];
    const dueDateId = proposal.formElements.find(
            (element) => element.id.toLowerCase() === "sponsor-deadline"
        )?.id,
        dueDate = proposalActualValues[dueDateId];

    const rtoString = proposal.formElements.find(
        (element) => element.id.toLowerCase() === "research-team"
    )?.answer;

    const { piUserId, piAffiliationCode } = await getResearchTeamData(rtoString);

    return {
        applicationType: "New",
        submissionType: "Application",
        proposalType: "proposal",
        proposalTitle: proposalTitle ?? proposal.projectTitle,
        proposalNumber: proposal.proposalNumber,
        referenceId: refId,
        opportunityServerKey: opportunity[0].opportunity.serverKey,
        dueDate,
        projectStartDate,
        projectEndDate,
        piUserId,
        piAffiliationCode,
    };
};

export const getS2SInstanceURL = async (tenantId) => {
    const response = await api.tenant.getTenantProducts(tenantId, 100);
    const proposalsProduct = response._embedded.tenant_products.find(
        (product) => product.name === PROPOSALS_PRODUCT_KEY
    );

    return proposalsProduct && proposalsProduct.instanceURL
        ? proposalsProduct.instanceURL.replace("login.do", "")
        : null;
};

export const getS2sItems = (proposalsS2SRecord, recordKey) => {
    if (!proposalsS2SRecord) {
        return [];
    }

    const items = [];
    const { url = null } = proposalsS2SRecord;

    for (const [key, value] of Object.entries(proposalsS2SRecord)) {
        if (key !== "url") {
            items.push({
                key,
                value,
                url: key === recordKey ? url : null,
            });
        }
    }

    return items;
};

export const getS2sConfig = (s2sLinks, config, dictionary) => {
    const extraSettings = {
        enableAutocomplete: true,
        dataSource: "Static",
        autocompleteMinLength: 3,
        multi: false,
        enabled: config.enabled,
        items: s2sLinks.map((item) => ({
            text: `${item.s2sProposalTitle} - ${item.s2sPiFullName}`,
            value: item,
        })),
    };

    return {
        ...config,
        type: FIELD_TYPES.SELECT,
        label: dictionary?.headline,
        i18n: {
            en_us: {
                label: dictionary?.headline,
            },
        },
        placeholder: dictionary.searchPlaceholder,
        tooltip: config.tooltip || config.extraSettings.tooltip,
        extraSettings,
        $original: {
            extraSettings: extraSettings,
        },
    };
};

export const buildCardItem = (s2s) => {
    return {
        [`${S2S_RECORD_KEY}`]: s2s.s2sPiFullName
            ? `${s2s.s2sProposalTitle} - ${s2s.s2sPiFullName}`
            : s2s.s2sProposalTitle,
        Status: s2s.submissionStatus,
        url: s2s.readOnly ? null : s2s.s2sProposalUrl,
    };
};
